import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  VStack,
  HStack,
  Radio,
  RadioGroup,
} from '@chakra-ui/react';
import AppColors from '../../../constants/AppColors';
import { AuditContext } from '../../../providers/AuditProvider';

function getChartFileCount(chart) {
  const hasOwnFile = chart?.file_obj ? 1 : 0;
  return hasOwnFile + (chart?.attachments || []).length;
}

const MergeChartsModal = ({ isOpen, onClose, charts = [], onMergeComplete }) => {
  const auditContext = useContext(AuditContext);

  const [primaryChartId, setPrimaryChartId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const defaultPrimary = charts[0]?.id;
      setPrimaryChartId(defaultPrimary != null ? String(defaultPrimary) : null);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const primaryChart = useMemo(
    () => charts.find(c => String(c.id) === String(primaryChartId)),
    [charts, primaryChartId]
  );
  const chartsToMerge = useMemo(
    () => charts.filter(c => String(c.id) !== String(primaryChartId)),
    [charts, primaryChartId]
  );

  const handleMerge = async () => {
    if (!primaryChart || chartsToMerge.length === 0) {
      return;
    }
    const response = await auditContext.mergeCharts(
      primaryChart.id,
      chartsToMerge.map(c => c.id)
    );
    if (!response?.error) {
      onMergeComplete && onMergeComplete();
    }
  };

  const isMerging = auditContext.isMergingCharts;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      isCentered
      closeOnOverlayClick={!isMerging}
      closeOnEsc={!isMerging}
    >
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent borderRadius="8px" overflow="hidden">
        <ModalHeader px={6} py={5} borderBottom="1px solid" borderColor="gray.100">
          <Text fontSize="18px" fontWeight={700} color={AppColors.secondary}>
            Merge Charts
          </Text>
          <Text fontSize="13px" fontWeight={400} color="gray.500">
            {charts.length} charts selected from this batch
          </Text>
        </ModalHeader>
        {!isMerging && <ModalCloseButton top="18px" />}
        <ModalBody px={6} py={5}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="13px" color="gray.600">
              Choose the chart that should stay. Its files will absorb the documents and notes
              from the other selected charts, which will then be removed.
            </Text>

            <RadioGroup
              value={primaryChartId ?? ''}
              onChange={setPrimaryChartId}
              isDisabled={isMerging}
            >
              <VStack align="stretch" spacing={2}>
                {charts.map(chart => {
                  const isPrimary = String(chart.id) === String(primaryChartId);
                  const fileCount = getChartFileCount(chart);
                  return (
                    <Box
                      key={chart.id}
                      p={3}
                      border="1px solid"
                      borderColor={isPrimary ? AppColors.secondary : 'gray.100'}
                      borderRadius="6px"
                      bg={isPrimary ? '#F0F5FF' : 'white'}
                    >
                      <HStack spacing={3} align="flex-start">
                        <Radio value={String(chart.id)} mt="2px" isDisabled={isMerging} />
                        <Box flex={1} minW={0}>
                          <Text fontSize="14px" fontWeight={600} color="gray.700" isTruncated>
                            {chart.name}
                          </Text>
                          <Text fontSize="12px" color="gray.500">
                            {fileCount} file{fileCount === 1 ? '' : 's'}
                            {isPrimary ? ' · Stays (merge target)' : ' · Will be merged in and deleted'}
                          </Text>
                        </Box>
                      </HStack>
                    </Box>
                  );
                })}
              </VStack>
            </RadioGroup>

            <Text fontSize="12px" color="gray.500">
              Note: any specialty, auditor, or QA assignment on the merged-away charts will be
              discarded, same as manually deleting them. Notes and documents carry over to the
              primary chart.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter px={6} py={4} bg="gray.50" borderTop="1px solid" borderColor="gray.100">
          <Button variant="ghost" onClick={onClose} isDisabled={isMerging}>
            Cancel
          </Button>
          <Button
            bg={AppColors.secondary}
            color="white"
            ml={3}
            onClick={handleMerge}
            isLoading={isMerging}
            loadingText="Merging"
            spinnerPlacement="end"
            isDisabled={!primaryChartId || chartsToMerge.length === 0}
            _hover={{ bg: AppColors.primaryColorHover || '#002968' }}
          >
            Merge {chartsToMerge.length || ''} Chart{chartsToMerge.length === 1 ? '' : 's'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MergeChartsModal;
